import { Link } from "react-router-dom";
import { PieChart } from "@mui/x-charts/PieChart";
import { hasRole } from "../../../components/Utils";
import { OvalLoader } from "../../../components/common/Loader";
import { CHART_PALETTE } from "../../../constant/theme";
import tysIcon from "../../../assets/images/icons/tys.png";
import oneToOneIcon from "../../../assets/images/icons/one_to_one.png";
import referralsIcon from "../../../assets/images/icons/referrals.png";
import visitorsIcon from "../../../assets/images/icons/visitors.png";

const chartHeight = 320;

const currencyFormatter = (val) => {
  const value = val?.value !== undefined ? val.value : val;
  if (value == null) return "";
  return `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(value)}`;
};

const countFormatter = (val) => {
  const value = val?.value !== undefined ? val.value : val;
  if (value == null) return "";
  return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(value);
};

const pieSlotProps = {
  legend: {
    direction: "column",
    position: { vertical: "middle", horizontal: "right" },
    itemMarkWidth: 10,
    itemMarkHeight: 10,
    markGap: 5,
    itemGap: 6,
  },
};

const pieSx = {
  "& .MuiPieArcLabel-root": {
    fill: "#fff",
    fontWeight: 300,
    fontSize: "13px",
  },
  "& .MuiChartsLegend-label": {
    fontSize: "0.65rem",
    fontWeight: 500,
  },
};

const ChapterPieCard = ({
  title,
  subtitle,
  imageIcon,
  iconBg,
  iconColor,
  loading,
  data,
  total,
  valueFormatter,
  reportLink,
  permission,
  hasPermission,
}) => {
  const canViewReport =
    reportLink && (!permission || !hasPermission || hasPermission(permission));

  const chartDataWithPct = data.map((item) => {
    const pct = total > 0 ? ((item.value / total) * 100).toFixed(1) : "0.0";
    return {
      ...item,
      label: `${item.label} - ${pct}%`,
    };
  });

  const legendMarginRight = Math.min(220, 130 + data.length * 6);

  return (
    <div className="chart-card">
      <div className="chart-card-header">
        <div className="chart-card-title-group">
          <div
            className="chart-card-icon"
            style={{ background: iconBg, color: iconColor }}
          >
            <img
              src={imageIcon}
              alt={title}
              className="chart-card-icon-image"
            />
          </div>
          <div>
            <h3 className="chart-card-title">{title}</h3>
            <p className="chart-card-subtitle-text">{subtitle}</p>
          </div>
        </div>
        {canViewReport && (
          <Link to={reportLink} className="chart-card-view-btn">
            <span>View Report</span>
            <i className="bx bx-right-arrow-alt"></i>
          </Link>
        )}
      </div>
      <div className="chart-card-body">
        {loading ? (
          <div className="chart-loader">
            <OvalLoader color={iconColor} />
          </div>
        ) : data.length > 0 ? (
          <PieChart
            colors={CHART_PALETTE}
            series={[
              {
                data: chartDataWithPct,
                innerRadius: 0,
                outerRadius: 100,
                paddingAngle: 2,
                cornerRadius: 4,
                highlightScope: { faded: "global", highlighted: "item" },
                faded: {
                  innerRadius: 0,
                  additionalRadius: -30,
                  color: "gray",
                },
                valueFormatter,
                arcLabel: (item) => {
                  const pct =
                    total > 0 ? ((item.value / total) * 100).toFixed(0) : 0;
                  return pct >= 5 ? `${pct}%` : "";
                },
                arcLabelMinAngle: 20,
              },
            ]}
            height={chartHeight}
            margin={{ top: 20, right: legendMarginRight, bottom: 20, left: 10 }}
            sx={pieSx}
            slotProps={pieSlotProps}
          />
        ) : (
          <div className="chart-empty">No data available</div>
        )}
      </div>
    </div>
  );
};

const ChapterWiseCharts = ({ data, ovalLoading, hasPermission }) => {
  if (!hasPermission("dashboard-tyns-data-view") || hasRole([3])) {
    return null;
  }

  const chartDataSet3 = (data.chapterWiseBusiness || [])
    .filter((item) => item.amount > 0)
    .map((item, index) => ({
      id: index,
      value: item.amount,
      label: item.chapter_name,
    }));

  const chartDataSet4 = (data.chapterWiseOneToOne || [])
    .filter((item) => item.count > 0)
    .map((item, index) => ({
      id: index,
      value: item.count,
      label: item.chapter_name,
    }));

  const chartDataSet5 = (data.chapterWiseReferrals || [])
    .filter((item) => item.count > 0)
    .map((item, index) => ({
      id: index,
      value: item.count,
      label: item.chapter_name,
    }));

  const chartDataSet6 = (data.chapterWiseVisitors || [])
    .filter((item) => item.count > 0)
    .map((item, index) => ({
      id: index,
      value: item.count,
      label: item.chapter_name,
    }));

  const totalBiz = chartDataSet3.reduce((sum, item) => sum + item.value, 0);
  const total121s = chartDataSet4.reduce((sum, item) => sum + item.value, 0);
  const totalRef = chartDataSet5.reduce((sum, item) => sum + item.value, 0);
  const totalVis = chartDataSet6.reduce((sum, item) => sum + item.value, 0);

  return (
    <div className="pie-charts-grid mb-4">
      <ChapterPieCard
        title="Chapter Wise Business"
        subtitle="Business generated by each chapter"
        imageIcon={tysIcon}
        iconBg="#fef3c7"
        iconColor="#d97706"
        loading={ovalLoading}
        data={chartDataSet3}
        total={totalBiz}
        valueFormatter={currencyFormatter}
        reportLink="/reports/chapter-wise-business"
        permission="reports-chapter-wise-business-list"
        hasPermission={hasPermission}
      />
      <ChapterPieCard
        title="Chapter Wise 121s"
        subtitle="121s completed by each chapter"
        imageIcon={oneToOneIcon}
        iconBg="#e0f2fe"
        iconColor="#0284c7"
        loading={ovalLoading}
        data={chartDataSet4}
        total={total121s}
        valueFormatter={countFormatter}
        reportLink="/reports/chapter-wise-one-to-one"
        permission="reports-chapter-wise-one-to-one-list"
        hasPermission={hasPermission}
      />
      <ChapterPieCard
        title="Chapter Wise Referrals"
        subtitle="Referrals given by each chapter"
        imageIcon={referralsIcon}
        iconBg="#ffedd5"
        iconColor="#ea580c"
        loading={ovalLoading}
        data={chartDataSet5}
        total={totalRef}
        valueFormatter={countFormatter}
        reportLink="/reports/chapter-wise-referrals"
        permission="reports-chapter-wise-referrals-list"
        hasPermission={hasPermission}
      />
      <ChapterPieCard
        title="Chapter Wise Visitors"
        subtitle="Visitors brought by each chapter"
        imageIcon={visitorsIcon}
        iconBg="#f3e8ff"
        iconColor="#9333ea"
        loading={ovalLoading}
        data={chartDataSet6}
        total={totalVis}
        valueFormatter={countFormatter}
        reportLink="/reports/chapter-wise-visitors"
        permission="reports-chapter-wise-visitors-list"
        hasPermission={hasPermission}
      />
    </div>
  );
};

export default ChapterWiseCharts;
